import React, { useState } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ChevronDown } from 'lucide-react';
import { ProfilePicture } from '@getro/rombo';
import PropTypes from 'prop-types';
import { JobApplicationDetail } from './jobApplicationDetail';

export const JobApplication = ({ application, isExpanded, onToggle }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatBoolean = (value) => {
    if (value === null) return '-';
    return value ? 'Yes' : 'No';
  };
  const [copied, setCopied] = useState(false);

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'border.subtle',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '140%',
      }}
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        py="16px"
        onClick={onToggle}
        sx={{
          cursor: 'pointer',
        }}
      >
        <Box
          flex="1"
          sx={{
            display: 'grid',
            gridTemplateColumns: '240px 240px 100px 1fr',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          <Text>{application.jobTitle}</Text>
          <Flex sx={{ alignItems: 'center', gap: '8px' }}>
            <Box
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
              }}
            >
              <ProfilePicture
                width="24px"
                height="24px"
                name={application.jobBoard}
                imageUrl={application.jobBoardLogo}
                size="small"
                variant="square"
                sx={{
                  borderRadius: '4px',
                }}
              />
            </Box>
            <Text>{application.jobBoard}</Text>
          </Flex>
          <Text fontSize="14px">{formatDate(application.applicationDate)}</Text>
        </Box>

        <Flex sx={{ alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
          <Text color="text.subtle" fontSize="14px" fontWeight="400" lineHeight="140%">
            {isExpanded ? 'Close' : 'See details'}
          </Text>
          <Box
            sx={{
              height: '16px',
              width: '16px',
              transition: 'transform 0.3s ease',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <Box as={ChevronDown} size={16} sx={{ color: 'text.subtle' }} />
          </Box>
        </Flex>
      </Flex>
      <Box
        sx={{
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          maxHeight: isExpanded ? '500px' : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <Box
          pb="16px"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'border.subtle',
          }}
        >
          <Flex flexDirection="column" sx={{ gap: '8px' }}>
            <JobApplicationDetail label="Date" value={formatDate(application.applicationDate)} />
            <JobApplicationDetail label="Job board" value={application.jobBoard || 'Network'} />
            <JobApplicationDetail label="First name" value={application.firstName} />
            <JobApplicationDetail label="Last name" value={application.lastName} />
            <JobApplicationDetail
              label="Email"
              value={application.email}
              copyText={copied ? 'Email copied!' : 'Copy email'}
              isLink
              onCopy={() => {
                navigator.clipboard.writeText(application.email);
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 2000);
              }}
            />
            <JobApplicationDetail label="LinkedIn profile URL" value={application.linkedinUrl} isLink isExternal />
            <JobApplicationDetail label="Personal website" value={application.website} isLink isExternal />
            <JobApplicationDetail
              label="Resume"
              value={application.resumeUrl ? 'View resume' : ''}
              isLink
              isExternal
              onClick={() => {
                const w = window.open(application.resumeUrl, '_blank', 'noopener,noreferrer');
                if (w) {
                  w.focus();
                }
              }}
            />
            <JobApplicationDetail
              label="Authorized to work in US?"
              value={formatBoolean(application.authorizedToWork)}
            />
            <JobApplicationDetail label="Will require visa?" value={formatBoolean(application.requiresVisa)} />
            <JobApplicationDetail label="Additional info" value={application.additionalInfo} />
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

JobApplication.propTypes = {
  application: PropTypes.shape({
    id: PropTypes.string.isRequired,
    jobTitle: PropTypes.string.isRequired,
    company: PropTypes.string.isRequired,
    companyLogo: PropTypes.string,
    companyLogoColor: PropTypes.string,
    applicationDate: PropTypes.string.isRequired,
    jobBoard: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    linkedinUrl: PropTypes.string,
    website: PropTypes.string,
    resumeUrl: PropTypes.string,
    authorizedToWork: PropTypes.bool,
    requiresVisa: PropTypes.bool,
    additionalInfo: PropTypes.string,
    jobBoardLogo: PropTypes.string,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
